// packages/frontend/src/hooks/data/useOnboardingChecklist.ts

import { useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiService } from '../../services/api.service';
import {
  dismissOnboardingChecklist,
  getOnboardingChecklist,
  recordChecklistExplicitCompletion,
  setOnboardingChecklistVisibility,
} from '../../services/api/workspace-admin.api';
import { useWorkspace } from '../../contexts/WorkspaceContext';
import { useCurrentUserQuery } from '../user/useUserQuery';
import { useNamespaceSocket } from '../../contexts/SocketContext';
import { safeAnalytics } from '../../utils/analytics';
import { useAppToast } from '../ui/useAppToast';
import { ANALYTICS_EVENTS } from '../../constants/analytics';
import { useFeatureFlag } from '../analytics/useFeatureFlag';
import { FEATURE_FLAGS } from '@bower/shared/constants/featureFlags';
import type {
  OnboardingChecklistResponse,
  OnboardingChecklistState,
  ChecklistExplicitCompletionKey,
} from '@bower/shared';
import { ONBOARDING_CHECKLIST_KEY } from '../../lib/queryClient';
export { ONBOARDING_CHECKLIST_KEY };

export class OnboardingChecklistWorkspaceError extends Error {
  constructor() {
    super('Cannot fetch onboarding checklist without a workspace');
    this.name = 'OnboardingChecklistWorkspaceError';
  }
}

export function shouldShowChecklist(state: OnboardingChecklistState | null): boolean {
  if (!state) return false;
  if (state.dismissedAt) return false;
  return true;
}

/**
 * True when the checklist state stored on the user row is already dismissed.
 * `/users/me` carries the raw state JSON, which lets us skip the checklist
 * GET entirely for users who finished onboarding long ago — previously every
 * such user refetched the checklist on every entity invalidation, forever.
 */
function checklistDismissedOnUser(onboardingChecklist: unknown): boolean {
  return (
    typeof onboardingChecklist === 'object' &&
    onboardingChecklist !== null &&
    'dismissedAt' in onboardingChecklist &&
    Boolean((onboardingChecklist as { dismissedAt?: unknown }).dismissedAt)
  );
}

/** Reads of a non-unified answer before the flag-widened fetch gives up. */
const NON_UNIFIED_TRIES = 3;

/** The checklist as cached: a unified list carries the workspace it is for. */
export type OnboardingChecklistData = OnboardingChecklistResponse & {
  readonly forWorkspaceId?: string;
  /** Answers in a row with no unified list, for the flag-widened fetch. */
  readonly nonUnifiedReads?: number;
};

/**
 * A unified list this workspace has finished with: dismissed, or complete
 * (nothing shows a complete list, hidden or not). Every entity invalidation
 * would otherwise refetch it, forever, for a list nobody sees. "Show setup
 * guide" clears the dismissal in the cache, which turns the query back on.
 */
function finishedHere(data: OnboardingChecklistData | undefined, workspaceId?: string): boolean {
  const unified = data?.unified;
  if (!unified || data.forWorkspaceId !== workspaceId) return false;
  return !!unified.dismissedAt || data.allCoreStepsComplete;
}

/**
 * The unified setup list's per-user flag as the client answers it, OR whether
 * the server has the user enrolled. A build with no PostHog key (staging)
 * never evaluates the client flag; `/users/me` answers `setupListEnabled` from
 * the stamped enrolment for that case.
 */
export function useUnifiedSetupFlag(): boolean {
  const clientFlag = useFeatureFlag(FEATURE_FLAGS.ONBOARDING_UNIFIED_SETUP);
  const { data: currentUser } = useCurrentUserQuery();
  return clientFlag || currentUser?.setupListEnabled === true;
}

export function useOnboardingChecklist() {
  const { workspace } = useWorkspace();
  const workspaceId = workspace?.id;
  const { data: currentUser } = useCurrentUserQuery();
  const queryClient = useQueryClient();
  const socket = useNamespaceSocket('notifications');

  // On the unified setup list the list lives on the workspace membership, so
  // the per-user state says nothing about this workspace: a user who dismissed
  // the old list, or a guest who never had one, still has a list here.
  // The server's answer too: a guest has no per-user list to fall back on.
  const unifiedSetup = useUnifiedSetupFlag();
  const perUserEligible =
    currentUser !== undefined &&
    currentUser.onboardingChecklist !== null &&
    !checklistDismissedOnUser(currentUser.onboardingChecklist);
  const enabled =
    !!workspaceId &&
    currentUser !== undefined &&
    currentUser.onboarded === true &&
    (unifiedSetup || perUserEligible);

  useEffect(() => {
    if (!enabled || !socket) return;
    const handler = () => {
      void queryClient.invalidateQueries({ queryKey: ONBOARDING_CHECKLIST_KEY });
    };
    socket.on('checklist:updated', handler);
    return () => {
      socket.off('checklist:updated', handler);
    };
  }, [enabled, socket, queryClient]);

  // The unified list belongs to ONE workspace, and the key carries none: a
  // switch through `setWorkspace` (create, recovery) does not clear the cache
  // the way `switchWorkspace` does, so drop the previous workspace's list.
  const shownFor = useRef(workspaceId);
  useEffect(() => {
    const previous = shownFor.current;
    shownFor.current = workspaceId;
    if (unifiedSetup && previous && previous !== workspaceId) {
      queryClient.removeQueries({ queryKey: ONBOARDING_CHECKLIST_KEY });
    }
  }, [workspaceId, unifiedSetup, queryClient]);

  return useQuery<OnboardingChecklistData>({
    queryKey: ONBOARDING_CHECKLIST_KEY,
    queryFn: async () => {
      if (!workspaceId) {
        throw new OnboardingChecklistWorkspaceError();
      }
      const data = await getOnboardingChecklist(apiService, workspaceId);
      // A unified list is one workspace's: say which, so a reader holding the
      // previous workspace's cached list for a render can tell.
      if (data.unified) return { ...data, forWorkspaceId: workspaceId };
      // Fetched only because the flag widened the fetch: count it, in the
      // cache every reader shares (a workspace change clears it). The
      // per-user response is otherwise returned untouched.
      if (perUserEligible) return data;
      const previous = queryClient.getQueryData<OnboardingChecklistData>(ONBOARDING_CHECKLIST_KEY);
      const inARow = previous && !previous.unified ? (previous.nonUnifiedReads ?? 0) : 0;
      return { ...data, nonUnifiedReads: inARow + 1 };
    },
    // The flag widens the fetch for a list that may not exist yet. An answer
    // with no unified list is the per-user one, and the per-user gate decides
    // whether that is worth refetching. Some such answers are permanent (a
    // support seat, an inactive workspace), but the server also enrols on
    // read, so a missed one (a flag read that timed out) repairs itself on a
    // later fetch: stop only after NON_UNIFIED_TRIES of them in a row.
    enabled: (query) => {
      const data = query.state.data;
      if (
        data &&
        !data.unified &&
        !perUserEligible &&
        (data.nonUnifiedReads ?? 0) >= NON_UNIFIED_TRIES
      ) {
        return false;
      }
      return enabled && !finishedHere(data, workspaceId);
    },
    staleTime: 30_000,
  });
}

export function useDismissChecklist() {
  const queryClient = useQueryClient();
  const toast = useAppToast();

  return useMutation({
    mutationFn: () => dismissOnboardingChecklist(apiService),
    onMutate: async () => {
      // Cancel any in-flight refetches so they don't overwrite our optimistic update
      await queryClient.cancelQueries({ queryKey: ONBOARDING_CHECKLIST_KEY });
      // Snapshot the previous value for rollback
      const previous =
        queryClient.getQueryData<OnboardingChecklistResponse>(ONBOARDING_CHECKLIST_KEY);
      // Optimistically set state to null so the checklist button hides immediately
      if (previous) {
        queryClient.setQueryData<OnboardingChecklistResponse>(ONBOARDING_CHECKLIST_KEY, {
          ...previous,
          state: null,
        });
      }
      return { previous };
    },
    onError: (_err, _vars, context) => {
      // Roll back to the snapshotted value on error, and tell the user —
      // without a toast the panel just silently reappears (PR #472 review).
      if (context?.previous) {
        queryClient.setQueryData(ONBOARDING_CHECKLIST_KEY, context.previous);
      }
      toast({ title: "Couldn't hide the checklist: please try again", status: 'error' });
    },
    onSuccess: () => {
      safeAnalytics.track(ANALYTICS_EVENTS.ONBOARDING_CHECKLIST_DISMISSED);
    },
    // Settled (not success-only): after an error rollback the cache still
    // holds the pre-mutation snapshot, which can be stale if the server
    // actually applied the dismiss (lost response). Refetch either way.
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ONBOARDING_CHECKLIST_KEY });
    },
  });
}

export function useRecordExplicitCompletion() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (stepKey: ChecklistExplicitCompletionKey) =>
      recordChecklistExplicitCompletion(apiService, stepKey),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ONBOARDING_CHECKLIST_KEY });
    },
  });
}

export type ChecklistVisibility = 'collapsed' | 'dismissed' | 'shown';

const VISIBILITY_EVENTS = {
  collapsed: ANALYTICS_EVENTS.ONBOARDING_CHECKLIST_COLLAPSED,
  dismissed: ANALYTICS_EVENTS.DASHBOARD_ONBOARDING_DISMISSED,
  shown: ANALYTICS_EVENTS.ONBOARDING_CHECKLIST_SHOWN_AGAIN,
} as const;

/**
 * Hide for now, don't show again, or show again, for this workspace's unified
 * setup list. Optimistic, rolled back with a toast on failure. The event is
 * tracked here, not in a caller's `mutate` options: the optimistic hide
 * unmounts the list that asked, and a caller's callbacks die with it.
 */
export function useSetChecklistVisibility() {
  const queryClient = useQueryClient();
  const toast = useAppToast();

  return useMutation({
    mutationFn: (visibility: ChecklistVisibility) =>
      setOnboardingChecklistVisibility(apiService, visibility),
    onMutate: async (visibility) => {
      await queryClient.cancelQueries({ queryKey: ONBOARDING_CHECKLIST_KEY });
      const previous =
        queryClient.getQueryData<OnboardingChecklistResponse>(ONBOARDING_CHECKLIST_KEY);
      if (previous?.unified) {
        const now = new Date().toISOString();
        queryClient.setQueryData<OnboardingChecklistResponse>(ONBOARDING_CHECKLIST_KEY, {
          ...previous,
          unified: {
            ...previous.unified,
            collapsedAt: visibility === 'collapsed' ? now : null,
            dismissedAt: visibility === 'dismissed' ? now : null,
          },
        });
      }
      return { previous };
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) {
        queryClient.setQueryData(ONBOARDING_CHECKLIST_KEY, context.previous);
      }
      toast({ title: "Couldn't update the setup list: please try again", status: 'error' });
    },
    onSuccess: (_data, visibility, context) => {
      safeAnalytics.track(VISIBILITY_EVENTS[visibility], {
        variant: context?.previous?.unified?.variant,
      });
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ONBOARDING_CHECKLIST_KEY });
    },
  });
}
